import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSupabaseClient } from 'src/api/supabase'
import { queryKeys } from 'src/api/db/queryKeys'
import { parseNextPath, paths } from 'src/routes/paths'
import { FormWrapper } from 'src/shared/components/FormComponents'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button, Card } from 'src/shared/components/ui'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { Footer } from 'src/shared/components/Footer'
import { formSchema, type SignupSuccessFormData } from './types'
import { useSignupSuccessData } from './hooks'
import { HeaderSection } from './components/HeaderSection'
import { CompanyInformationSection } from './components/CompanyInformationSection'
import { PersonalInformationSection } from './components/PersonalInformationSection'
import { LoadingState } from './components/LoadingState'
import { InvalidState } from './components/InvalidState'
import { PartyPopper } from 'lucide-react'

export type SignupSuccessPanelProps = {
  orgId: string | null
  variant?: 'page' | 'embedded'
  /** Called instead of navigate after successful submit (e.g. my-account dialog) */
  onSuccess?: () => void
}

export const SignupSuccessPanel = ({
  orgId,
  variant = 'page',
  onSuccess,
}: SignupSuccessPanelProps) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { t } = useLocalization()
  const { toast } = useToast()
  const supabase = useSupabaseClient()

  const { organization, user, updateOrganization, updateUser } =
    useSignupSuccessData(orgId)

  const methods = useForm<SignupSuccessFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: organization?.name || '',
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
    },
  })

  useEffect(() => {
    if (organization && user) {
      methods.reset({
        companyName: organization.name || '',
        firstName: user.first_name || '',
        lastName: user.last_name || '',
      })
    }
  }, [organization, user, methods])

  const handleSubmit = async (data: SignupSuccessFormData) => {
    if (!organization?.id || !user?.id) {
      toast({
        title: t('Error', 'Fel'),
        description: t(
          'Missing organization or user data',
          'Saknar organisations- eller användardata'
        ),
        variant: 'destructive',
      })
      return
    }

    try {
      await updateOrganization.mutateAsync({
        id: organization.id,
        name: data.companyName,
      })

      const { data: authData, error: getUserError } =
        await supabase.auth.getUser()
      if (getUserError) {
        console.error(
          'Failed to read auth user before profile update:',
          getUserError
        )
      }
      const authEmail = authData?.user?.email?.trim()

      await updateUser.mutateAsync({
        id: user.id,
        first_name: data.firstName,
        last_name: data.lastName || null,
        ...(authEmail ? { email: authEmail } : {}),
      })

      const { error: authUpdateError } = await supabase.auth.updateUser({
        data: {
          first_name: data.firstName,
          last_name: data.lastName ?? '',
        },
      })
      if (authUpdateError) {
        console.error(
          'Failed to sync auth user on signup success:',
          authUpdateError
        )
      }

      const { error: refreshError } = await supabase.auth.refreshSession()
      if (refreshError) {
        console.error(
          'Failed to refresh session after signup success:',
          refreshError
        )
      }

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.organization],
      })
      await queryClient.invalidateQueries({ queryKey: [queryKeys.user] })
      await queryClient.invalidateQueries({ queryKey: [queryKeys.org_user] })
      await queryClient.invalidateQueries({
        queryKey: ['orgUserByOrgId', organization.id],
      })

      toast({
        title: t('Success', 'Lyckades!'),
        description: t(
          'Information updated successfully',
          'Informationen uppdaterades'
        ),
        variant: 'success',
      })

      if (onSuccess) {
        onSuccess()
      } else {
        navigate(parseNextPath(searchParams.get('next')) ?? paths.myAccount)
      }
    } catch (error) {
      toast({
        title: t('Error', 'Fel'),
        description:
          error instanceof Error
            ? error.message
            : t(
                'Failed to update information',
                'Kunde inte uppdatera informationen'
              ),
        variant: 'destructive',
      })
    }
  }

  if (!orgId) {
    return <InvalidState />
  }

  if (!organization || !user) {
    return <LoadingState />
  }

  const formCard = (
    <Card className="w-full p-8 glass">
      <FormWrapper methods={methods} onSubmit={handleSubmit}>
        <Column className="gap-6">
          <HeaderSection />

          <CompanyInformationSection methods={methods} />

          <PersonalInformationSection methods={methods} />

          <Row className="justify-center gap-4 pt-4">
            <Button type="submit" variant="primary" size="lg">
              Done
              <PartyPopper className="w-4 h-4 ml-2" />
            </Button>
          </Row>
        </Column>
      </FormWrapper>
    </Card>
  )

  if (variant === 'embedded') {
    return (
      <Column className="w-full max-w-4xl mx-auto px-4 py-6 overflow-y-auto min-h-0 max-h-full flex-1">
        {formCard}
      </Column>
    )
  }

  return (
    <>
      <Column className="w-full min-h-dvh items-center justify-center relative bg-theme_light py-12 overflow-hidden">
        <Column className="gap-8 w-full max-w-4xl px-4 relative z-10">
          {formCard}
        </Column>
      </Column>

      <Footer />
    </>
  )
}
